import ReactSlider from 'react-slider';
import { Header, Segment } from 'semantic-ui-react';
import { SUPPORTED_YEARS } from '../../../../constnats/generalConstants';
import ToggleGroup from '../../../../components/toggleGroup';
import { ConfigProps, KeyValueOptions } from '../../../../components/financialTables/configuration/constants';
import { MultiselectField, SelectFieldWrapper } from '../../../../components/financialTables/configuration/fields';
import '../../../../components/financialTables/configuration/styles.css';

const currentYear = new Date().getFullYear();

type ComparisonConfigFormProps = {
    filterCfg: Record<string, any>;
    tableConfig: Record<string, any>;
    onFilterChange: (cfg: Record<string, unknown>) => void;
    onTableConfigChange: (cfg: Record<string, unknown>) => void;
    granularPeriodsFiltering: boolean;
};

function ComparisonConfigForm({
    filterCfg,
    tableConfig,
    onFilterChange,
    onTableConfigChange,
    granularPeriodsFiltering,
}: ComparisonConfigFormProps) {
    const onFilterPropChange = (prop: string, val: unknown) => {
        onFilterChange(Object.assign({}, filterCfg, { [prop]: val }));
    };

    const onTablePropChange = (prop: string, val: unknown) => {
        onTableConfigChange(Object.assign({}, tableConfig, { [prop]: val }));
    };

    return (
        <Segment className="tableConfigSegment baseSettings" size="mini">
            <div className="base-settings-wrapper flex-container">
                <div className="flex-container--item min-w-100 years-filter">
                    <YearsFilter
                        config={filterCfg[ConfigProps.YEARS_FILTER]}
                        onChange={(val: { from: number; to: number }) =>
                            onFilterPropChange(ConfigProps.YEARS_FILTER, val)
                        }
                    />
                </div>

                <div className="flex-container--item periods-filter">
                    <PeriodFilter
                        config={filterCfg[ConfigProps.PERIODS_FILTER]}
                        onChange={(val: string[]) => onFilterPropChange(ConfigProps.PERIODS_FILTER, val)}
                        granularPeriodsFiltering={granularPeriodsFiltering}
                    />
                </div>

                <div className="flex-container--item report-types-filter">
                    <ReportTypeFilter
                        config={filterCfg[ConfigProps.REPORT_TYPES_FILTER]}
                        onChange={(val: string[]) => onFilterPropChange(ConfigProps.REPORT_TYPES_FILTER, val)}
                    />
                </div>

                <div className="flex-container--item">
                    <FillingTypeFilter
                        config={filterCfg[ConfigProps.FILLING_TYPE_FILTER]}
                        onChange={(val: string[]) => onFilterPropChange(ConfigProps.FILLING_TYPE_FILTER, val)}
                    />
                </div>

                <div className="flex-container--item">
                    <ConditionalFilteringFilter
                        config={filterCfg}
                        onChange={(_p: string, val: string) => {
                            onFilterPropChange(ConfigProps.ORIGINAL_VERSION_CONDITIONAL_FILTER, val);
                        }}
                    />
                </div>

                <div className="flex-container--item order">
                    <Order
                        config={filterCfg}
                        onPropChange={(_p: string, val: string) => {
                            onFilterPropChange(ConfigProps.REPORTS_ORDER, val);
                        }}
                    />
                </div>

                <div className="flex-container--item">
                    <Notes config={tableConfig} onPropChange={onTablePropChange} />
                </div>

                <div className="flex-container--item">
                    <MissingDataInLatest config={tableConfig} onPropChange={onTablePropChange} />
                </div>
            </div>
        </Segment>
    );
}

function Notes({ config, onPropChange }: { config: Record<string, any>; onPropChange: (prop: string, val: unknown) => void }) {
    return (
        <div className="config-module">
            <ModuleHeader text="Notes" />
            <ToggleGroup
                activeValue={!!config[ConfigProps.SHOW_NOTES]}
                onChange={(value: boolean) => onPropChange(ConfigProps.SHOW_NOTES, value)}
            >
                <ToggleGroup.Button value={true}>Show</ToggleGroup.Button>
                <ToggleGroup.Button value={false}>Hide</ToggleGroup.Button>
            </ToggleGroup>
        </div>
    );
}

function MissingDataInLatest({ config, onPropChange }: { config: Record<string, any>; onPropChange: (prop: string, val: unknown) => void }) {
    return (
        <div className="config-module">
            <ModuleHeader text="Missing Data in Latest" />
            <ToggleGroup
                activeValue={config[ConfigProps.SHOW_ORIGINAL_CELL_DATA_IF_LATEST_MISSING]}
                onChange={(value: boolean) => onPropChange(ConfigProps.SHOW_ORIGINAL_CELL_DATA_IF_LATEST_MISSING, value)}
            >
                <ToggleGroup.Button value={true}>Show Original</ToggleGroup.Button>
                <ToggleGroup.Button value={false}>Empty</ToggleGroup.Button>
            </ToggleGroup>
        </div>
    );
}

function Order({ config, onPropChange }: { config: Record<string, any>; onPropChange: (prop: string, val: string) => void }) {
    return (
        <div className="configModule">
            <ModuleHeader text="Order" />
            <ToggleGroup
                activeValue={config[ConfigProps.REPORTS_ORDER]}
                onChange={(val: string) => onPropChange(ConfigProps.REPORTS_ORDER, val)}
            >
                {KeyValueOptions.SORTING.map((option: any) => (
                    <ToggleGroup.Button key={option.key} value={option.key}>
                        {option.value}
                    </ToggleGroup.Button>
                ))}
            </ToggleGroup>
        </div>
    );
}

function PeriodFilter({ config, onChange, granularPeriodsFiltering }: { config: string[]; onChange: (val: string[]) => void; granularPeriodsFiltering: boolean }) {
    return (
        <div className="configModule">
            <ModuleHeader text="Periods" />
            <div className="configModule-body">
                <MultiselectField
                    selected={config}
                    optionsKV={
                        granularPeriodsFiltering
                            ? KeyValueOptions.GRANULAR_PERIODS_FILTER_OPS
                            : KeyValueOptions.PERIODS_FILTER_OPS
                    }
                    onChange={(x: string[]) => onChange(x)}
                />
            </div>
        </div>
    );
}

function ReportTypeFilter({ config, onChange }: { config: string[]; onChange: (val: string[]) => void }) {
    return (
        <div className="configModule">
            <ModuleHeader text="Formats" />
            <div className="configModule-body">
                <MultiselectField
                    selected={config}
                    optionsKV={KeyValueOptions.REPORT_TYPE_FILTER}
                    onChange={onChange}
                />
            </div>
        </div>
    );
}

function FillingTypeFilter({ config, onChange }: { config: string[]; onChange: (val: string[]) => void }) {
    return (
        <div className="configModule">
            <ModuleHeader text="Versions" />
            <div className="configModule-body">
                <MultiselectField
                    selected={config}
                    optionsKV={KeyValueOptions.FILLING_TYPE_FILTER}
                    onChange={onChange}
                />
            </div>
        </div>
    );
}

function ConditionalFilteringFilter({ config, onChange }: { config: Record<string, any>; onChange: (prop: string, val: string) => void }) {
    return (
        <div className="configModule">
            <ModuleHeader text="Hide Original if" />
            <SelectFieldWrapper
                className="max-w-120 margin-l-r-auto"
                label=""
                config={config}
                prop={ConfigProps.ORIGINAL_VERSION_CONDITIONAL_FILTER}
                opsKV={KeyValueOptions.ORIGINAL_VERSION_CONDITIONAL_FILTER}
                onChange={onChange}
            />
        </div>
    );
}

function YearsFilter({ config, onChange }: { config: { from: number; to: number }; onChange: (val: { from: number; to: number }) => void }) {
    return (
        <div className="configModule">
            <ModuleHeader text="Years" />
            <ReactSlider
                className="horizontal-slider"
                thumbClassName="slider-thumb"
                trackClassName="slider-track"
                defaultValue={[Number(SUPPORTED_YEARS[0]), currentYear + 1]}
                renderThumb={(props: any, state: any) => <div {...props}>{state.valueNow}</div>}
                pearling
                value={[config.from, config.to]}
                minDistance={0}
                min={Number(SUPPORTED_YEARS[0])}
                max={currentYear + 1}
                onAfterChange={(s: number[]) => onChange({ from: s[0], to: s[1] })}
            />
        </div>
    );
}

function ModuleHeader({ text }: { text: string }) {
    return (
        <Header as="h5" textAlign="center">
            {text}
        </Header>
    );
}

export default ComparisonConfigForm;
